import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import { CcRequirementReviewMethod, CcRequirementReviewMethodCreate } from "server/models"
import { useInput, StateType } from "client/utils"
import {
    StoreType,
    CcRequirementReviewMethodValidation,
    SaveCcRequirementReviewMethod,
    closeModal,
    setCcRequirementReviewMethodEdit,
    setCcRequirementReviewMethodReloadList,
    setCcRequirementReviewMethodReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    SelectDropdown,
    SelectDropdownItem,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface ReviewMethodEditProps {
    method: CcRequirementReviewMethod
    editAccess: boolean
}

let isMounted: boolean

const Form = ({
    method,
    editAccess,
}: ReviewMethodEditProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(method.name)
    const [description, setDescription] = useInput(method.description)
    const [isDefault, setDefault] = useState(method.default)
    const [validation, setValidation] = useState<CcRequirementReviewMethodValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newMethodData: CcRequirementReviewMethodCreate = {
            accountId: method.accountId || userData.accountId,
            projectId: method.projectId || project.id,
            name,
            description,
            creator: method.id ? method.creator : userData.userId,
            default: isDefault,
        }

        SaveCcRequirementReviewMethod(method.id, newMethodData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setCcRequirementReviewMethodEdit({
                ...newMethodData,
                id: res.newMethodId || method.id,
            }))

            dispatch(setCcRequirementReviewMethodReloadList(false))
            setTimeout(() => {
                dispatch(setCcRequirementReviewMethodReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setCcRequirementReviewMethodReloadEdit(false))
                setTimeout(() => {
                    dispatch(setCcRequirementReviewMethodReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={!editAccess} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} readOnly={!editAccess} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.default}</Label>
                        {
                            editAccess && (
                                <SelectDropdown title={isDefault ? lang.yes : lang.no}>
                                    <SelectDropdownItem onClick={() => setDefault(true)}>{lang.yes}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setDefault(false)}>{lang.no}</SelectDropdownItem>
                                </SelectDropdown>
                            )
                        }
                        {
                            !editAccess && (
                                <Input type="text" value={isDefault ? lang.yes : lang.no} readOnly />
                            )
                        }
                        <Feedback>{validation && validation.default}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        editAccess && [
                            <Button key="save" variant="success" row onClick={() => onSave(1)}>
                                {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                            </Button>,
                            <Button key="apply" variant="success" row onClick={() => onSave(2)}>
                                {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                            </Button>,
                        ]
                    }
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
